import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faDownload, faTrash, faPaperPlane, faFile, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button/Button';
import DataTable, { ColumnDef } from '../../components/DataTable';
import Pagination from '../../components/Pagination/Pagination';
import ToastNotification from '../../components/toast_notification';
import { documentosService, DownloadItem } from './services/documentosService';
import '../../styles/analytical-table.css';
import './style.css';

interface ToastState {
    status: number;
    message: string;
}

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (value: string): string => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString('pt-BR');
};

const Documentos = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [downloads, setDownloads] = useState<DownloadItem[]>([]);
    const [loadingList, setLoadingList] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [copiedToken, setCopiedToken] = useState<string | null>(null);
    const [toast, setToast] = useState<ToastState | null>(null);

    const [sortField, setSortField] = useState<string>('createdAt');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const carregarArquivos = async () => {
        try {
            setLoadingList(true);
            const lista = await documentosService.listarArquivos();
            setDownloads(lista);
            setCurrentPage(0);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao carregar os arquivos.';
            setToast({ status: 500, message });
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        carregarArquivos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilesPicked = (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const novos = Array.from(fileList);
        setSelectedFiles(prev => {
            const existentes = new Set(prev.map(f => `${f.name}-${f.size}`));
            const semDuplicados = novos.filter(f => !existentes.has(`${f.name}-${f.size}`));
            return [...prev, ...semDuplicados];
        });
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleClear = () => {
        setSelectedFiles([]);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setToast({ status: 400, message: 'Selecione ao menos um arquivo para enviar.' });
            return;
        }

        try {
            setUploading(true);
            await documentosService.uploadArquivos(selectedFiles);
            setToast({ status: 200, message: 'Arquivos enviados e link gerado com sucesso.' });
            handleClear();
            await carregarArquivos();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao enviar arquivos.';
            setToast({ status: 500, message });
        } finally {
            setUploading(false);
        }
    };

    const handleCopyLink = async (token: string) => {
        const link = documentosService.getPublicUrl(token);
        try {
            await navigator.clipboard.writeText(link);
            setCopiedToken(token);
            setTimeout(() => setCopiedToken(prev => (prev === token ? null : prev)), 2000);
        } catch {
            setToast({ status: 400, message: 'Não foi possível copiar o link automaticamente.' });
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        handleFilesPicked(e.dataTransfer.files);
    };

    const handleSort = (field: string) => {
        setSortDirection(prev => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
        setSortField(field);
        setCurrentPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortField) return downloads;
        const sorted = [...downloads].sort((a, b) => {
            const av = a[sortField as keyof DownloadItem];
            const bv = b[sortField as keyof DownloadItem];
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'number' && typeof bv === 'number') return av - bv;
            if (typeof av === 'boolean' && typeof bv === 'boolean') return av === bv ? 0 : av ? 1 : -1;
            return String(av).localeCompare(String(bv), 'pt-BR');
        });
        return sortDirection === 'asc' ? sorted : sorted.reverse();
    }, [downloads, sortField, sortDirection]);

    const totalElements = sortedData.length;
    const totalPages = Math.ceil(totalElements / pageSize);

    const paginatedData = useMemo(
        () => sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
        [sortedData, currentPage, pageSize]
    );

    const columns = useMemo((): ColumnDef<DownloadItem>[] => [
        {
            key: 'nomeArquivo',
            label: 'Arquivo',
            sortable: true,
            render: item => <span className="documentos-file-name">{item.nomeArquivo}</span>,
        },
        {
            key: 'nomesOriginais',
            label: 'Conteúdo',
            sortable: true,
            render: item => (
                <span className="documentos-cell-originais" title={item.nomesOriginais}>
                    {item.nomesOriginais}
                </span>
            ),
        },
        {
            key: 'tamanhoBytes',
            label: 'Tamanho',
            sortable: true,
            render: item => formatFileSize(item.tamanhoBytes),
        },
        {
            key: 'createdAt',
            label: 'Criado em',
            sortable: true,
            render: item => formatDateTime(item.createdAt),
        },
        {
            key: 'expiraEm',
            label: 'Expira em',
            sortable: true,
            render: item => formatDateTime(item.expiraEm),
        },
        {
            key: 'ativo',
            label: 'Status',
            sortable: true,
            render: item => (
                <span className={`status-pill ${item.ativo ? 'status-green' : 'status-red'}`}>
                    {item.ativo ? 'Ativo' : 'Expirado'}
                </span>
            ),
        },
    ], []);

    return (
        <div className="documentos-container">
            <div className="page-header">
                <div className="page-title-section">
                    <h1 className="page-title">Documentos</h1>
                    <p className="page-subtitle">
                        Envie os arquivos para gerar um link de download que pode ser copiado e encaminhado.
                    </p>
                </div>
            </div>

            <div className="documentos-card">
                <div
                    className="documentos-dropzone"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                >
                    <FontAwesomeIcon icon={faUpload} className="documentos-dropzone-icon" />
                    <span className="documentos-dropzone-text">
                        Clique para selecionar ou arraste os arquivos aqui
                    </span>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="documentos-file-input"
                        onChange={(e) => handleFilesPicked(e.target.files)}
                    />
                </div>

                {selectedFiles.length > 0 && (
                    <ul className="documentos-file-list">
                        {selectedFiles.map((file, index) => (
                            <li key={`${file.name}-${file.size}-${index}`} className="documentos-file-item">
                                <div className="documentos-file-info">
                                    <FontAwesomeIcon icon={faFile} className="documentos-file-icon" />
                                    <span className="documentos-file-name">{file.name}</span>
                                    <span className="documentos-file-size">{formatFileSize(file.size)}</span>
                                </div>
                                <button
                                    type="button"
                                    className="documentos-file-remove"
                                    onClick={() => handleRemoveFile(index)}
                                    title="Remover"
                                    disabled={uploading}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="documentos-actions">
                    <Button
                        variant="secondary"
                        icon={faTrash}
                        onClick={handleClear}
                        disabled={uploading || selectedFiles.length === 0}
                    >
                        Limpar
                    </Button>
                    <Button
                        variant="primary"
                        icon={faPaperPlane}
                        onClick={handleUpload}
                        disabled={uploading || selectedFiles.length === 0}
                    >
                        {uploading ? 'Enviando...' : 'Enviar arquivos'}
                    </Button>
                </div>
            </div>

            <h3 className="documentos-section-title">Links gerados</h3>
            <DataTable<DownloadItem>
                data={paginatedData}
                columns={columns}
                loading={loadingList}
                loadingText="Carregando arquivos..."
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                rowKey={item => item.token}
                rowClassName={item => (item.ativo ? '' : 'disabled-row')}
                emptyTitle="Nenhum arquivo gerado"
                emptyMessage="Envie arquivos acima para gerar um link de download."
                emptyHint=""
                actionsLabel=""
                renderActions={item => (
                    <div className="documentos-row-actions">
                        <Button
                            variant="secondary"
                            icon={copiedToken === item.token ? faCheck : faCopy}
                            onClick={() => handleCopyLink(item.token)}
                        >
                            {copiedToken === item.token ? 'Copiado!' : 'Copiar'}
                        </Button>
                        <a
                            href={documentosService.getPublicUrl(item.token)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="registros-button registros-button--primary documentos-link-open"
                        >
                            <div className="registros-button__text">
                                <FontAwesomeIcon icon={faDownload} className="registros-button__icon" />
                                Abrir
                            </div>
                        </a>
                    </div>
                )}
            />

            {toast && (
                <ToastNotification
                    status={toast.status}
                    message={toast.message}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default Documentos;
